import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["violationsList", "modal", "modalContent"]

  connect() {
    console.log("🔗 Dashboard controller connected to element:", this.element)
    console.log("🔗 Dashboard controller targets:", this.targets)
    
    // Make this controller instance globally available for manual fallback
    window.dashboardController = this;
    
    this.setupEventListeners()
    this.setupActionCable()
    this.setupFilters()
    
    // Check for violation elements on page load
    setTimeout(() => {
      const violationElements = document.querySelectorAll('[data-violation-id]')
      console.log(`🔍 Found ${violationElements.length} violation elements on page`)
      if (violationElements.length > 0) {
        console.log('🔍 First violation element:', violationElements[0])
        console.log('🔍 Sample violation data:', {
          id: violationElements[0].dataset.violationId,
          severity: violationElements[0].dataset.violationSeverity,
          user: violationElements[0].dataset.violationUser
        });
      }
    }, 500)
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  setupEventListeners() {
    console.log("🎧 Setting up dashboard event listeners")
    // Ensure DOM is ready before setting up event listeners
    const setupListeners = () => {
      console.log("🎧 Setting up violation click listeners")
      // Modal close listeners - click outside to close
      document.addEventListener('click', (e) => {
        // Close modal when clicking outside
        if (e.target.matches('#violation-modal')) {
          this.closeModal()
        }
      })

      // Violation click listeners
      document.addEventListener('click', (e) => {
        // Check if click is on a button or interactive element
        if (e.target.closest('button, a, input, select, textarea')) {
          return;
        }
        
        const violationItem = e.target.closest('[data-violation-id]')
        if (violationItem) {
          e.preventDefault();
          e.stopPropagation();
          const violationId = violationItem.dataset.violationId
          console.log('🖱️ Violation row clicked:', violationId)
          this.showViolationDetails(violationId, violationItem)
        }
      })

      // ESC key to close modal
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeModal()
        }
      })
    }

    // Set up listeners when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupListeners)
    } else {
      setupListeners()
    }
  }

  setupActionCable() {
    const consumer = createConsumer()
    
    this.subscription = consumer.subscriptions.create("ViolationsChannel", {
      connected() {
        console.log("Connected to ViolationsChannel")
        this.perform("request_violations_summary")
      },

      disconnected() {
        console.log("Disconnected from ViolationsChannel")
      },

      received: (data) => {
        console.log("Received data:", data)
        
        if (data.type === "violations_summary") {
          this.updateDashboardMetrics(data.summary)
        } else if (data.type === "new_violation") {
          this.addNewViolation(data.violation)
        } else if (data.type === "violation_resolved") {
          this.markViolationResolved(data.violation_id)
        }
      }
    })
  }

  setupFilters() {
    // Status filter
    const statusFilter = document.getElementById('status-filter')
    if (statusFilter) {
      statusFilter.addEventListener('change', () => this.applyFilters())
    }

    // Severity filter  
    const severityFilter = document.getElementById('severity-filter')
    if (severityFilter) {
      severityFilter.addEventListener('change', () => this.applyFilters())
    }

    // Platform filter
    const platformFilter = document.getElementById('platform-filter')
    if (platformFilter) {
      platformFilter.addEventListener('change', () => this.applyFilters())
    }

    // Date filter
    const dateFilter = document.getElementById('date-filter')
    if (dateFilter) {
      dateFilter.addEventListener('change', () => this.applyFilters())
    }

    // Search input
    const searchInput = document.getElementById('search-input')
    if (searchInput) {
      searchInput.addEventListener('input', () => this.applyFilters())
    }
  }

  applyFilters() {
    const statusFilter = document.getElementById('status-filter')?.value || 'all'
    const severityFilter = document.getElementById('severity-filter')?.value || 'all'  
    const platformFilter = document.getElementById('platform-filter')?.value || 'all'
    const dateFilter = document.getElementById('date-filter')?.value || 'all'
    const searchInput = document.getElementById('search-input')?.value.toLowerCase() || ''

    const violationItems = document.querySelectorAll('[data-violation-id]')
    
    violationItems.forEach(item => {
      let show = true

      // Status filter
      if (statusFilter !== 'all') {
        const isResolved = item.querySelector('.bg-slate-600') !== null
        if (statusFilter === 'resolved' && !isResolved) show = false
        if (statusFilter === 'unresolved' && isResolved) show = false
      }

      // Severity filter
      if (severityFilter !== 'all') {
        const severity = item.dataset.violationSeverity
        if (severity !== severityFilter) show = false
      }

      // Platform filter
      if (platformFilter !== 'all') {
        const platform = item.dataset.violationPlatform
        if (platform !== platformFilter) show = false
      }

      // Date filter
      if (dateFilter !== 'all') {
        const createdAt = new Date(item.dataset.violationCreated)
        const now = new Date()
        const diffTime = Math.abs(now - createdAt)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

        if (dateFilter === 'today' && diffDays > 1) show = false
        if (dateFilter === 'week' && diffDays > 7) show = false
        if (dateFilter === 'month' && diffDays > 30) show = false
      }

      // Search filter
      if (searchInput) {
        const text = item.textContent.toLowerCase()
        if (!text.includes(searchInput)) show = false
      }

      item.style.display = show ? 'flex' : 'none'
    })
  }

  showViolationDetails(violationId, violationElement) {
    console.log('📝 showViolationDetails called with ID:', violationId);
    
    // Wait for DOM to be ready if modal elements aren't available yet
    const checkModalAndShow = () => {
      const modal = document.getElementById('violation-modal')
      const modalContent = document.getElementById('modal-content')
      
      console.log('🔍 Modal elements check:', { modal: !!modal, modalContent: !!modalContent });
      
      if (!modal || !modalContent) {
        console.log('⏱️ Modal elements not ready, retrying...');
        // If modal elements don't exist yet, try again after a short delay
        setTimeout(checkModalAndShow, 100)
        return
      }
      
      console.log('✅ Modal elements ready, showing details');
      this.doShowViolationDetails(violationId, violationElement, modal, modalContent)
    }
    
    checkModalAndShow()
  }

  doShowViolationDetails(violationId, violationElement, modal, modalContent) {

    // Extract data from the violation element
    const violationData = {
      id: violationId,
      severity: violationElement.dataset.violationSeverity,
      platform: violationElement.dataset.violationPlatform, 
      user: violationElement.dataset.violationUser,
      created: violationElement.dataset.violationCreated,
      rule: violationElement.querySelector('h4').textContent,
      reason: violationElement.querySelector('.text-slate-300').textContent,
      dept: violationElement.querySelector('.text-slate-400:nth-of-type(2)')?.textContent?.replace('Dept: ', '') || 'None',
      content: JSON.parse(this.decodeHtmlEntities(violationElement.dataset.violationContent || '""')),
      blockedContent: JSON.parse(this.decodeHtmlEntities(violationElement.dataset.violationBlockedContent || '""'))
    }

    // Populate modal content
    modalContent.innerHTML = this.generateModalContent(violationData)
    
    // Show modal
    modal.classList.remove('hidden')
    modal.classList.add('flex')
    modal.style.display = 'flex'  // Force show in case of CSS conflicts
    
    // Focus trap
    setTimeout(() => {
      const closeButton = modal.querySelector('[data-action*="closeModal"]')
      if (closeButton) closeButton.focus()
    }, 100)
  }

  decodeHtmlEntities(str) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  }

  generateModalContent(data) {
    return `
      <div class="bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between p-6 border-b border-slate-700">
          <h3 class="text-xl font-semibold text-white">Violation Details</h3>
          <button onclick="window.closeViolationModal()" class="text-slate-400 hover:text-white p-1 rounded">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="p-6 space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Policy Rule</label>
                <p class="text-white font-medium">${data.rule}</p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Severity</label>
                <span class="inline-flex px-3 py-1 text-sm font-medium rounded-full ${this.getSeverityClasses(data.severity)}">
                  ${data.severity.charAt(0).toUpperCase() + data.severity.slice(1)}
                </span>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Platform</label>
                <p class="text-white">${data.platform.charAt(0).toUpperCase() + data.platform.slice(1)}</p>
              </div>
            </div>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">User</label>
                <p class="text-white">${data.user}</p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Department</label>
                <p class="text-white">${data.dept}</p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Date & Time</label>
                <p class="text-white">${data.created}</p>
              </div>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Violation Reason</label>
            <div class="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <p class="text-slate-300">${data.reason}</p>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Original Prompt</label>
            <div class="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <p class="text-slate-300 whitespace-pre-wrap">${data.content || 'No content available'}</p>
            </div>
          </div>
          
          ${data.blockedContent && data.blockedContent !== data.content ? `
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Blocked Content</label>
            <div class="bg-red-900/20 rounded-lg p-4 border border-red-700/50">
              <p class="text-red-300 whitespace-pre-wrap">${data.blockedContent}</p>
            </div>
          </div>
          ` : ''}
          
          <div class="flex justify-end space-x-3 pt-4 border-t border-slate-700">
            <button onclick="window.closeViolationModal()" class="px-4 py-2 text-slate-300 hover:text-white transition-colors">
              Close
            </button>
            <button onclick="resolveViolation(this, '${data.id}')" class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors">
              Mark as Resolved  
            </button>
          </div>
        </div>
      </div>
    `
  }

  getSeverityClasses(severity) {
    switch(severity) {
      case 'critical':
        return 'bg-red-500/20 text-red-300'
      case 'high':
        return 'bg-orange-500/20 text-orange-300'
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-300'
      default:
        return 'bg-slate-500/20 text-slate-300'
    }
  }

  closeModal() {
    if (window.closeViolationModal) {
      window.closeViolationModal()
    } else {
      // Fallback if global function isn't available
      const modal = document.getElementById('violation-modal')
      if (modal) {
        modal.classList.add('hidden')
        modal.classList.remove('flex')
        modal.style.display = 'none'
      }
    }
  }

  markViolationResolved(violationId) {
    const violationElement = document.querySelector(`[data-violation-id="${violationId}"]`)
    
    if (violationElement) {
      // Find and update the resolve button
      const resolveButton = violationElement.querySelector('[data-action*="resolveViolation"]')
      if (resolveButton) {
        resolveButton.outerHTML = '<span class="px-3 py-1 bg-slate-600 text-slate-300 text-sm rounded">Resolved</span>'
      }
      
      // Apply current filters to hide/show as needed
      this.applyFilters()
    }
  }

  updateDashboardMetrics(summary) {
    // Update metric counters if they exist
    const totalElement = document.getElementById('total-violations')
    if (totalElement) totalElement.textContent = summary.total_unresolved || 0

    const criticalElement = document.getElementById('critical-violations')
    if (criticalElement) criticalElement.textContent = summary.critical_count || 0

    const todayElement = document.getElementById('today-violations')
    if (todayElement) todayElement.textContent = summary.today_count || 0
  }

  addNewViolation(violation) {
    // Add new violation to the list if on violations page
    const violationsList = document.getElementById('violations-list')
    if (violationsList) {
      // This would require creating the HTML for the new violation
      console.log('New violation received:', violation)
      // Refresh the page or dynamically add the violation
      window.location.reload()
    }
  }

  async updateViolationMetrics() {
    try {
      const response = await fetch('/dashboard/real_time_alerts')
      const data = await response.json()
      
      this.updateElement('total-critical', data.total_critical)
      this.updateElement('total-high', data.total_high)
      this.updateElement('total-medium', data.total_medium)
      this.updateElement('total-low', data.total_low)
      this.updateElement('today-count', data.today_count)
      this.updateElement('week-count', data.week_count)
      
    } catch (error) {
      console.error('Error updating metrics:', error)
    }
  }

  updateElement(id, value) {
    const element = document.getElementById(id)
    if (element) {
      element.textContent = value || 0
    }
  }
}

// Global function for closing violation modal
window.closeViolationModal = function() {
  console.log('closeViolationModal called')
  const modal = document.getElementById('violation-modal')
  if (modal) {
    console.log('Modal found, closing...')
    modal.classList.add('hidden')
    modal.classList.remove('flex')
    modal.style.display = 'none'  // Force hide in case of CSS conflicts
    console.log('Modal closed')
  } else {
    console.error('Modal not found!')
  }
}

// Global function for resolve buttons
window.resolveViolation = async function(button, violationId) {
  try {
    button.disabled = true
    button.textContent = 'Resolving...'
    
    const response = await fetch(`/dashboard/violations/${violationId}/resolve`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content')
      }
    })
    
    if (response.ok) {
      button.outerHTML = '<span class="px-3 py-1 bg-slate-600 text-slate-300 text-sm rounded">Resolved</span>'
      
      // Close modal if open
      window.closeViolationModal()
      
      // Apply filters to update display
      if (window.dashboardController) {
        window.dashboardController.applyFilters()
      }
    } else {
      throw new Error('Failed to resolve violation')
    }
  } catch (error) {
    console.error('Error resolving violation:', error)
    button.disabled = false
    button.textContent = 'Resolve'
    alert('Failed to resolve violation. Please try again.')
  }
}

